import { Trans, useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibTopLevelAlert } from '@chewy/kib-messaging-react'
import { KibModal } from '@chewy/kib-overlays-react'
import { Text } from '@pbt/pbt-ui-components'

import type { InventoryOrderItem } from '~/api/graphql/generated/types'
import i18n from '~/locales/i18n'
import { getInventoryItemDisplayName } from '~/utils/inventoryItemUtils'

import {
  FormattedQuantityDisplay,
  type FormattedQuantityDisplayVariant,
} from '../FormattedQuantityDisplay'

import styles from './OrderItemReceiptConfirmModal.module.scss'

export interface Quantities {
  receiving: number
  receivingDamaged: number
  receivingOverage: number
  rejectingDamaged: number
  rejectingExpired: number
  rejectingOther: number
  rejectingOverage: number
  rejectingRecalled: number
  remaining: number
}

interface QuantityConfig {
  key: keyof Quantities
  label: string
  variant: FormattedQuantityDisplayVariant
}

const createQuantityConfig = (
  key: keyof Quantities,
  label: string,
  variant: FormattedQuantityDisplayVariant,
): QuantityConfig => ({
  key,
  label,
  variant,
})
const quantityConfig: QuantityConfig[] = [
  createQuantityConfig(
    'receiving',
    i18n.t('Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.RECEIVING_LABEL'),
    'receiving',
  ),
  createQuantityConfig(
    'receivingDamaged',
    i18n.t(
      'Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.RECEIVING_AS_DAMAGED_LABEL',
    ),
    'receiving',
  ),
  createQuantityConfig(
    'receivingOverage',
    i18n.t(
      'Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.RECEIVING_AS_OVERAGE_LABEL',
    ),
    'receiving',
  ),
  createQuantityConfig(
    'rejectingDamaged',
    i18n.t(
      'Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.REJECTING_AS_DAMAGED_LABEL',
    ),
    'rejecting',
  ),
  createQuantityConfig(
    'rejectingOverage',
    i18n.t(
      'Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.REJECTING_AS_OVERAGE_LABEL',
    ),
    'rejecting',
  ),
  createQuantityConfig(
    'rejectingExpired',
    i18n.t(
      'Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.REJECTING_AS_EXPIRED_LABEL',
    ),
    'rejecting',
  ),
  createQuantityConfig(
    'rejectingRecalled',
    i18n.t(
      'Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.REJECTING_AS_RECALLED_LABEL',
    ),
    'rejecting',
  ),
  createQuantityConfig(
    'rejectingOther',
    i18n.t('Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.REJECTING_AS_OTHER_LABEL'),
    'rejecting',
  ),
  createQuantityConfig(
    'remaining',
    i18n.t('Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.REMAINING_IN_ORDER_LABEL'),
    'remaining',
  ),
]

interface OrderItemReceiptConfirmModalProps {
  item: InventoryOrderItem
  onClose: () => void
  onMakeChanges: () => void
  onReceive: () => void
  open: boolean
  quantities: Quantities
}

const quantityKeyToLabel: Partial<Record<keyof Quantities, string>> = {
  rejectingOverage: i18n.t('Admin:CATALOG.ORDERS.REASON_OVERAGE'),
  rejectingDamaged: i18n.t('Admin:CATALOG.ORDERS.REASON_DAMAGED'),
  rejectingExpired: i18n.t('Admin:CATALOG.ORDERS.REASON_EXPIRED'),
  rejectingRecalled: i18n.t('Admin:CATALOG.ORDERS.REASON_RECALLED'),
  rejectingOther: i18n.t('Admin:CATALOG.ORDERS.REASON_OTHER'),
}

const getFormattedReasons = (quantities: Quantities) => {
  const rejectionKeys = Object.keys(quantities) as (keyof Quantities)[]
  const activeRejectionReasons = rejectionKeys.reduce((acc, key) => {
    if (quantityKeyToLabel[key] && quantities[key] > 0) {
      acc.push(quantityKeyToLabel[key])
    }
    return acc
  }, [] as string[])

  return new Intl.ListFormat(i18n.language, {
    style: 'long',
    type: 'disjunction',
  }).format(activeRejectionReasons)
}

export const OrderItemReceiptConfirmModal = ({
  open,
  onClose,
  onReceive,
  onMakeChanges,
  item,
  quantities,
}: OrderItemReceiptConfirmModalProps) => {
  const { t } = useTranslation(['Admin', 'Common'])

  const controls = () => (
    <>
      <KibButtonNew emphasis="primary" onClick={onReceive}>
        {t('Common:CONFIRM_ACTION')}
      </KibButtonNew>
      <KibButtonNew emphasis="secondary" onClick={onMakeChanges}>
        {t('Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.MAKE_CHANGES_BUTTON')}
      </KibButtonNew>
    </>
  )

  const isPartiallyReceived = quantities.remaining > 0
  const hasOverage = quantities.receivingOverage > 0
  const hasRejections =
    quantities.rejectingOverage > 0 ||
    quantities.rejectingDamaged > 0 ||
    quantities.rejectingExpired > 0 ||
    quantities.rejectingRecalled > 0 ||
    quantities.rejectingOther > 0

  return (
    <KibModal
      className={styles.modal}
      controls={controls}
      open={open}
      title={t('Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.TITLE')}
      onClose={onClose}
    >
      <div className={styles.alertContainer}>
        {hasRejections && (
          <KibTopLevelAlert
            heading={t(
              'Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.WARNING_REJECTED_UNITS',
            )}
            severity="warning"
          />
        )}
        {hasOverage && (
          <KibTopLevelAlert
            heading={t(
              'Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.WARNING_OVERAGE_UNITS',
            )}
            severity="warning"
          />
        )}
      </div>

      <div className={styles.itemHeader}>
        {getInventoryItemDisplayName(item.inventoryItem)}
      </div>

      <div className={styles.quantityGrid}>
        {quantityConfig.map(
          ({ key, label, variant }) =>
            quantities[key] > 0 && (
              <div className={styles.quantityItem} key={key}>
                <span className={styles.quantityLabel}>{label}</span>
                <FormattedQuantityDisplay
                  packageTypeName={item.packageType.name}
                  packageUnitsName={item.packageUnits.name}
                  perPackageAmount={item.perPackageAmount}
                  quantity={quantities[key]}
                  variant={variant}
                />
              </div>
            ),
        )}
      </div>

      <div className={styles.infoSection}>
        {hasOverage && (
          <Text>
            {t(
              'Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.RECEIVED_OVERAGE_INFO',
            )}
          </Text>
        )}

        {isPartiallyReceived && (
          <Text>
            <Trans
              components={{ boldSpan: <strong /> }}
              i18nKey="Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.PARTIALLY_RECEIVED_INFO"
            />
          </Text>
        )}

        {hasRejections && (
          <>
            <Text>
              <Trans
                components={{ boldSpan: <strong /> }}
                i18nKey="Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.REJECTED_UNITS_INFO"
                values={{ reasons: getFormattedReasons(quantities) }}
              />
            </Text>
            <Text>
              <Trans
                components={{ boldSpan: <strong /> }}
                i18nKey="Admin:CATALOG.ORDERS.CONFIRM_ENTRY_MODAL.SOP_REMINDER"
              />
            </Text>
          </>
        )}
      </div>
    </KibModal>
  )
}
